import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography
} from '@mui/material';
import { findProjects } from 'api/services/ProjectsService';
import {
  FilesTypesForUxbConcepts,
  FilesTypesForUxbIngredients,
  FilesTypes
} from 'api/types/ReferenceData';
import { trackSearchEvent } from 'piwik/piwik';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ContactRequest,
  LastRequest,
  RequestTypes
} from 'types/ContactRequests';
import { DocumentsDisplay } from '../Documents';
import {
  ValidPrefillData,
  documentTypesToObjectArr,
  formatProducts,
  removeDuplicateDocumentTypes
} from './helpers/helpers';
import { submitRequestByContext } from './helpers/requestsSubmit';
import { Asset } from 'types/Asset';
import { IS_UXBEAUTY, currentPlatform } from 'components/constants/Constants';
import { ProductType } from 'types/Products';
import BaseRequestModal from './BaseRequestModal';
import { RequestContactProps } from './types';
import { useBrandPortalEventKey } from 'hooks/useBrandPortalEventKey';
import {
  CustomContactSection,
  CustomContactSectionFormState
} from './CustomContactSection/CustomContactSection';

export interface DocumentTypes {
  value: string;
  label: string;
}

type RequestDocumentDialogFormState = {
  acknowledge: boolean;
  request: {
    message: string;
    documentTypes?: DocumentTypes[];
    otherDocumentType: string;
  };
} & CustomContactSectionFormState;

const RequestDocumentDialog = ({
  show,
  setModal,
  fullProducts,
  context,
  project,
  requestType,
  documents,
  searchContext,
  productType
}: // searchRequest
RequestContactProps) => {
  const brandPortalKey = useBrandPortalEventKey();
  const methods = useForm<RequestDocumentDialogFormState>();
  const {
    register,
    handleSubmit,
    getValues,
    control,
    setValue,
    formState: { errors },
    watch
  } = methods;

  const [submitting, setSubmitting] = useState(false);

  const i18n = useTranslation();
  const [closeVisible, setCloseVisible] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<any>([]);
  const [allowAddDocuments, setAllowAddDocuments] = useState(false);
  const [lastRequest, setLastRequest] = useState<LastRequest>();
  const [lastDocumentTypes, setLastDocumentTypes] = useState<DocumentTypes[]>(
    []
  );
  const productsArray = Array.isArray(fullProducts)
    ? fullProducts
    : [fullProducts];
  const isGivaudanProduct =
    productsArray.findIndex(product => product.company.key === 'givaudan') >= 0;
  useEffect(() => {
    setAllowAddDocuments(isGivaudanProduct);
  }, [isGivaudanProduct]);
  const loadLastRequest = () => {
    if (!lastRequest) return;
    const { message, documentTypes, otherDocumentType } = lastRequest;
    const documentTypesObjectArr = documentTypesToObjectArr(documentTypes);
    setLastDocumentTypes(documentTypesObjectArr);
    setSelectedTypes(
      removeDuplicateDocumentTypes([
        ...selectedTypes,
        ...documentTypesObjectArr
      ])
    );
    setValue('request', {
      message: message,
      otherDocumentType: otherDocumentType,
      documentTypes: removeDuplicateDocumentTypes(documentTypesObjectArr)
    });
  };

  const submitData = () => {
    setSubmitting(true);
    if (searchContext) {
      trackSearchEvent('request-document-by-form', fullProducts[0].name);
    }
    const values = getValues();
    const body: Partial<ContactRequest> = {
      type: 'DOCUMENT' as RequestTypes,
      request: {
        ...values.request,
        documentTypes: values.request.documentTypes?.map(type => type.value)
      },
      products: formatProducts(context, fullProducts)
    };

    submitRequestByContext(
      context,
      body,
      requestType,
      setModal,
      setSubmitting,
      project
    );
  };

  const closeModals = () => {
    setAllowAddDocuments(false);
    setModal(false);
  };

  if (
    !isGivaudanProduct &&
    documents &&
    documents.length > 0 &&
    allowAddDocuments === false
  ) {
    return (
      <DisplayDocuments
        allowAddDocuments={allowAddDocuments}
        setAllowAddDocuments={setAllowAddDocuments}
        closeModals={closeModals}
        documents={documents}
        product={fullProducts}
        searchContext={searchContext}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(submitData)} data-request-type={requestType}>
      <BaseRequestModal
        show={show}
        title="Request documents"
        onSubmitRequest={submitData}
        setModal={() => setModal(false)}
        requestType={requestType}
        setLastRequest={setLastRequest}
        loadLastRequest={loadLastRequest}
        closeVisible={closeVisible}
        setCloseVisible={setCloseVisible}
        submitting={submitting}
        modalSize="lg"
      >
        <DialogContentText>
          {i18n.t(
            'contactCenter.detail.manage_request.new_request_top_message'
          )}
        </DialogContentText>
        <ValidPrefillData data={lastRequest}>
          <DialogContentText>
            {i18n.t(
              'contactCenter.detail.manage_request.new_request_top_message2'
            )}{' '}
            {requestType.toLowerCase()} request.
          </DialogContentText>
        </ValidPrefillData>
        {fullProducts?.length <= 1 && (
          <DialogContentText>
            {i18n.t(
              'contactCenter.detail.manage_request.new_request_top_message3'
            )}
            <span className="fw-bolder"> {fullProducts[0]?.name} </span>
          </DialogContentText>
        )}
        <ValidPrefillData data={lastRequest}>
          <Button
            sx={{ my: 2 }}
            onClick={() => {
              setCloseVisible(true);
            }}
            variant="outlined"
            size="small"
            color="success"
          >
            Prefill
          </Button>
        </ValidPrefillData>

        {brandPortalKey !== undefined && fullProducts.length === 1 && (
          <FormProvider {...methods}>
            <CustomContactSection brandPortalKey={brandPortalKey} />
          </FormProvider>
        )}

        <Typography variant="h6" gutterBottom>
          Request Details
        </Typography>

        <Box>
          <Box data-testid="documentTypes">
            <Controller
              name="request.documentTypes"
              rules={{ required: true }}
              render={({ field, field: { value } }) => {
                return (
                  <Autocomplete
                    {...field}
                    multiple
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    options={getDocumentTypeOptions(productType)}
                    onChange={(_, values) => {
                      field.onChange(values);
                      setSelectedTypes(values);
                      setLastDocumentTypes([]);
                    }}
                    value={
                      ((lastDocumentTypes.length > 1 && lastDocumentTypes) ||
                        value) ??
                      []
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        fullWidth
                        label="File type"
                        margin="dense"
                        error={!!errors.request}
                        helperText={
                          errors.request ? 'This field is required.' : ''
                        }
                      />
                    )}
                  />
                );
              }}
              control={control}
            />
          </Box>
          {selectedTypes.find((i: any) => i.value === 'OTHER') && (
            <TextField
              data-testid="otherTypes"
              label="Other types"
              fullWidth
              margin="dense"
              {...register('request.otherDocumentType')}
              placeholder="Type 1, Type 2, ..."
            />
          )}
        </Box>
        <TextField
          label="Intention of request"
          multiline
          rows={3}
          fullWidth
          margin="dense"
          placeholder={
            "e.g. I'm interested in having this document for my new skin care line"
          }
          {...register('request.message')}
          value={watch('request.message') ?? ''}
          data-testid="intentionOfRequest"
        />

        {project && (
          <RequestWithProject register={register} project={project} />
        )}

        <FormControl error={!!errors.acknowledge}>
          <FormControlLabel
            required
            control={
              <Checkbox
                id="acknowledge"
                {...register('acknowledge', { required: true })}
              />
            }
            label={`I allow ${currentPlatform} to store my information for the purpose given in this contact form.`}
            data-testid="acknowledge"
          />
          {errors?.acknowledge && (
            <FormHelperText>This field is required.</FormHelperText>
          )}
        </FormControl>
      </BaseRequestModal>
    </form>
  );
};
export default RequestDocumentDialog;
interface RequestWithProjectProps {
  register: any;
  project: any | null;
}

/**
 * Special case for requests triggered via the projects center
 */
const RequestWithProject = ({ register, project }: RequestWithProjectProps) => {
  const [shareProject, setShareProject] = useState(false);

  useEffect(() => {
    findProjects();
  }, []);
  return (
    <Box>
      <FormControl>
        <FormControlLabel
          required
          {...register('project.include')}
          control={
            <Checkbox onChange={e => setShareProject(e.target.checked)} />
          }
          label="Share project name and description"
        />
      </FormControl>

      {shareProject && (
        <>
          <Typography variant="h6">{project.title}</Typography>
          <TextField
            fullWidth
            placeholder={project.description}
            defaultValue={project.description}
            {...register('project.description')}
          />
        </>
      )}
    </Box>
  );
};

interface ModalDisplayDocuments {
  allowAddDocuments: boolean;
  setAllowAddDocuments: Function;
  documents: Asset[];
  closeModals: Function;
  product: any;
  searchContext?: boolean;
  // products: any;
  // searchRequest: any;
}

const DisplayDocuments = ({
  allowAddDocuments,
  setAllowAddDocuments,
  documents,
  closeModals,
  product,
  searchContext
}: ModalDisplayDocuments) => {
  return (
    <Dialog
      open={!allowAddDocuments}
      onClose={() => closeModals()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h3">Documents available</Typography>
      </DialogTitle>

      <DialogContent>
        <DocumentsDisplay
          data={product}
          documents={documents}
          searchContext={searchContext}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={() => closeModals()}>Close</Button>
        <Button variant="contained" onClick={() => setAllowAddDocuments()}>
          Request other documents
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const getDocumentTypeOptions = (productType: ProductType) => {
  if (IS_UXBEAUTY) {
    if (productType === ProductType.FORMULATIONS) {
      return FilesTypesForUxbConcepts;
    } else {
      return FilesTypesForUxbIngredients;
    }
  }
  return FilesTypes;
};
